import {
    Box,
    Chip,
    CircularProgress,
    Link,
    Paper,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { trackEvent } from "../../../services/GoogleAnalytics";
import { usePagesTranslation } from "../../locales";
import VocabularyService from "./VocabularyService";

function playPathForEntry(entry) {
    const type = entry?.puzzleType;
    if (type === "CROSSWORD" && entry?.sourceCross?.length) {
        return `/puzzle/play-cross/${entry.sourceCross[0]}`;
    }
    if ((type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") && entry?.sourceGrid?.length) {
        return type === "HIDDEN_MESSAGE"
            ? `/puzzle/play-hidden-message/${entry.sourceGrid[0]}`
            : `/puzzle/play-word-search/${entry.sourceGrid[0]}`;
    }
    return null;
}

const VocabularyMain = () => {
    const { t } = usePagesTranslation();
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        trackEvent("entered", { name: "VocabularyMain" });
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await VocabularyService.getVocabulary(page, PAGE_SIZE);
                setRows(result.content || []);
                setTotalPages(result.totalPages || 1);
                setTotalElements(result.totalElements || 0);
            } catch (err) {
                setError(err.message || "Failed to load vocabulary");
                setRows([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [page]);

    const getPuzzleTypeLabel = (puzzleType) => {
        switch (puzzleType) {
            case "WORD_SEARCH":
                return t("profile.history.puzzle-type-word-search");
            case "HIDDEN_MESSAGE":
                return t("profile.history.puzzle-type-hidden-message");
            case "CROSSWORD":
                return t("profile.history.puzzle-type-crossword");
            default:
                return puzzleType || "—";
        }
    };

    const formatSavedAt = (ms) => {
        if (ms == null) return "—";
        try {
            return new Date(ms).toLocaleString();
        } catch {
            return "—";
        }
    };

    if (loading && rows.length === 0) {
        return (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && rows.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Paper sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        {t("profile.vocabulary.error-loading")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                {t("profile.vocabulary.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t("profile.vocabulary.subtitle")}
            </Typography>

            <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("profile.vocabulary.total", { count: totalElements })}
                </Typography>

                {rows.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                            {t("profile.vocabulary.no-words")}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small" aria-label={t("profile.vocabulary.title")}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("profile.vocabulary.word")}</TableCell>
                                        <TableCell>{t("profile.vocabulary.language")}</TableCell>
                                        <TableCell>{t("profile.vocabulary.puzzle-type")}</TableCell>
                                        <TableCell>{t("profile.vocabulary.saved-at")}</TableCell>
                                        <TableCell align="right">{t("profile.vocabulary.puzzle-link")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => {
                                        const playPath = playPathForEntry(row);
                                        return (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    <Typography fontWeight={600}>{row.word}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {row.language ? (
                                                        <Chip label={row.language.toUpperCase()} size="small" variant="outlined" />
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                                <TableCell>{getPuzzleTypeLabel(row.puzzleType)}</TableCell>
                                                <TableCell>{formatSavedAt(row.createdAt)}</TableCell>
                                                <TableCell align="right">
                                                    {playPath ? (
                                                        <Link component={RouterLink} to={playPath} underline="hover">
                                                            {t("profile.vocabulary.open-puzzle")}
                                                        </Link>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totalPages > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_, value) => setPage(value - 1)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default VocabularyMain;
